import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  labelIcon?: ReactNode;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  /**
   * Trailing adornment (e.g. a search glyph). Purely decorative and
   * click-through — use a real button if it ever needs to be actionable.
   */
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      labelIcon,
      error,
      hint,
      leftIcon,
      rightIcon,
      className = '',
      id,
      ...props
    },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    return (
      <div className="flex flex-col gap-2 w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="text-sm font-medium text-foreground leading-5 flex items-center gap-1"
          >
            {labelIcon}
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-icon-default pointer-events-none">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={[
              'w-full h-10 rounded-md border bg-white text-foreground text-sm leading-5',
              'px-3 py-2',
              'placeholder:text-text-placeholder',
              'focus:outline-2 focus:outline-offset-0 focus:outline-border-focus focus:border-border-focus',
              'disabled:bg-disabled-bg disabled:text-disabled-text disabled:cursor-not-allowed',
              'transition-colors duration-150',
              // Resting border is `border/neutral/default-subtle` (#e5e5e5),
              // the token the shared Figma `.field` binds — not the stronger
              // #d1d1d1 this shipped with.
              //
              // The error tone is #a82222 (`border/error/default`). It is NOT
              // `danger` (#c94040), which maps to no Figma variable at all.
              //
              // Whether the border should redden at all is genuinely open: the
              // SMS onboarding frames ("Enter number / error", 9639:93885)
              // leave the field neutral and redden only the hint copy, yet the
              // design system does define a full `border/error/*` ramp. Until a
              // frame settles it, the affordance stays and only the hex is
              // corrected.
              error ? 'border-border-error' : 'border-border',
              leftIcon ? 'pl-10' : '',
              rightIcon ? 'pr-10' : '',
              className,
            ]
              .filter(Boolean)
              .join(' ')}
            aria-invalid={error ? true : undefined}
            aria-describedby={
              error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined
            }
            {...props}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-icon-default pointer-events-none">
              {rightIcon}
            </span>
          )}
        </div>
        {error && (
          <p
            id={`${inputId}-error`}
            className="text-sm text-text-error leading-5"
            role="alert"
          >
            {error}
          </p>
        )}
        {!error && hint && (
          <p
            id={`${inputId}-hint`}
            className="text-sm text-muted-foreground leading-5"
          >
            {hint}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = 'Input';
